import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { algoService, AlgoSignal, STRATEGIES } from '../../services/algoService';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from 'recharts';
import { canAccessAutoTrader } from '../../lib/entitlements';
import '../Dashboard.css';

function fmtCurrency(num: number | null | undefined): string {
  if (num === null || num === undefined) return '—';
  return (num >= 0 ? '+' : '') + '$' + Math.abs(num).toFixed(2);
}

function fmtPct(num: number): string {
  return num.toFixed(1) + '%';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
}

// ─── Equity curve computation ────────────────────────────────────────────────

interface EquityPoint { date: string; cumPnl: number; symbol: string; pnl: number }

function buildEquityCurve(signals: AlgoSignal[]): EquityPoint[] {
  const closed = signals
    .filter(s => s.trade?.status === 'closed' && s.trade.exitAt && s.trade.pnl != null)
    .sort((a, b) => new Date(a.trade!.exitAt!).getTime() - new Date(b.trade!.exitAt!).getTime());
  let cum = 0;
  return closed.map(s => {
    cum += s.trade!.pnl!;
    return { date: s.trade!.exitAt!, cumPnl: Math.round(cum * 100) / 100, symbol: s.trade!.symbol, pnl: s.trade!.pnl! };
  });
}

function calcMaxDrawdown(curve: EquityPoint[]): number {
  let peak = 0, maxDd = 0;
  for (const pt of curve) {
    if (pt.cumPnl > peak) peak = pt.cumPnl;
    const dd = peak - pt.cumPnl;
    if (dd > maxDd) maxDd = dd;
  }
  return maxDd;
}

// ─── Metrics computation ────────────────────────────────────────────────────

interface DirectionMetrics {
  label: string;
  closedCount: number;
  totalPnl: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  avgHoldHrs: number;
}

interface StrategyMetrics {
  strategyId: string;
  name: string;
  closedCount: number;
  winRate: number;
  avgPnl: number;
  totalPnl: number;
  bestTrade: number;
  worstTrade: number;
}

interface ExitReasonCount {
  reason: string;
  count: number;
  avgPnl: number;
  color: string;
}

function computeDirectionMetrics(signals: AlgoSignal[], direction: 'long' | 'short' | 'all'): DirectionMetrics {
  const closed = signals.filter(s => {
    if (s.trade?.status !== 'closed' || s.trade.pnl == null) return false;
    if (direction === 'all') return true;
    return (s.direction || 'long') === direction;
  });
  const pnls = closed.map(s => s.trade!.pnl!);
  const wins = pnls.filter(p => p > 0);
  const losses = pnls.filter(p => p < 0);
  const grossWins = wins.reduce((a, b) => a + b, 0);
  const grossLosses = Math.abs(losses.reduce((a, b) => a + b, 0));

  // Avg hold time in hours
  const holdHours = closed.map(s => {
    const entry = new Date(s.trade!.entryAt).getTime();
    const exit = new Date(s.trade!.exitAt!).getTime();
    return (exit - entry) / (1000 * 60 * 60);
  });
  const avgHoldHrs = holdHours.length > 0 ? holdHours.reduce((a, b) => a + b, 0) / holdHours.length : 0;

  return {
    label: direction === 'all' ? 'All' : direction === 'long' ? 'Long' : 'Short',
    closedCount: closed.length,
    totalPnl: pnls.reduce((a, b) => a + b, 0),
    winRate: closed.length > 0 ? (wins.length / closed.length) * 100 : 0,
    avgWin: wins.length > 0 ? grossWins / wins.length : 0,
    avgLoss: losses.length > 0 ? grossLosses / losses.length : 0,
    profitFactor: grossLosses > 0 ? grossWins / grossLosses : grossWins > 0 ? Infinity : 0,
    avgHoldHrs,
  };
}

function computeStrategyMetrics(signals: AlgoSignal[]): StrategyMetrics[] {
  const byStrategy: Record<string, AlgoSignal[]> = {};
  signals.filter(s => s.trade?.status === 'closed' && s.trade.pnl != null).forEach(s => {
    const sid = s.strategyId || 'unknown';
    if (!byStrategy[sid]) byStrategy[sid] = [];
    byStrategy[sid].push(s);
  });

  return Object.entries(byStrategy).map(([sid, sigs]) => {
    const pnls = sigs.map(s => s.trade!.pnl!);
    const wins = pnls.filter(p => p > 0).length;
    const stratInfo = STRATEGIES[sid as keyof typeof STRATEGIES];
    return {
      strategyId: sid,
      name: stratInfo?.name || sid,
      closedCount: sigs.length,
      winRate: sigs.length > 0 ? (wins / sigs.length) * 100 : 0,
      avgPnl: pnls.reduce((a, b) => a + b, 0) / sigs.length,
      totalPnl: pnls.reduce((a, b) => a + b, 0),
      bestTrade: Math.max(...pnls),
      worstTrade: Math.min(...pnls),
    };
  }).sort((a, b) => b.totalPnl - a.totalPnl);
}

const EXIT_REASON_COLORS: Record<string, string> = {
  take_profit: '#4ade80',
  trailing_stop: '#60a5fa',
  profit_protection: '#a78bfa',
  stop_loss: '#f87171',
  max_drawdown: '#dc2626',
  manual: '#fbbf24',
};

function computeExitReasons(signals: AlgoSignal[]): ExitReasonCount[] {
  const byReason: Record<string, { count: number; totalPnl: number }> = {};
  signals.filter(s => s.trade?.status === 'closed' && s.trade.exitReason).forEach(s => {
    const reason = s.trade!.exitReason || 'unknown';
    if (!byReason[reason]) byReason[reason] = { count: 0, totalPnl: 0 };
    byReason[reason].count++;
    byReason[reason].totalPnl += s.trade!.pnl || 0;
  });
  return Object.entries(byReason).map(([reason, data]) => ({
    reason: reason.replace(/_/g, ' '),
    count: data.count,
    avgPnl: data.count > 0 ? data.totalPnl / data.count : 0,
    color: EXIT_REASON_COLORS[reason] || '#6b7280',
  })).sort((a, b) => b.count - a.count);
}

function computeSharpe(signals: AlgoSignal[]): number {
  // Daily returns from closed trades
  const byDate: Record<string, number> = {};
  signals.filter(s => s.trade?.status === 'closed' && s.trade.exitAt && s.trade.pnl != null).forEach(s => {
    const date = s.trade!.exitAt!.split('T')[0];
    byDate[date] = (byDate[date] || 0) + s.trade!.pnl!;
  });
  const dailyReturns = Object.values(byDate);
  if (dailyReturns.length < 5) return 0;
  const mean = dailyReturns.reduce((a, b) => a + b, 0) / dailyReturns.length;
  const variance = dailyReturns.reduce((sum, r) => sum + (r - mean) ** 2, 0) / dailyReturns.length;
  const stdDev = Math.sqrt(variance);
  if (stdDev === 0) return 0;
  return (mean / stdDev) * Math.sqrt(252); // annualized
}

// ─── Chart error boundary ────────────────────────────────────────────────────

class ChartErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return <div style={{ padding: '8px 0', fontSize: 12, color: '#64748b' }}>Chart unavailable</div>;
    return this.props.children;
  }
}

// ─── Stat card ───────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string; color?: string; sub?: string }> = ({ label, value, color = '#f9fafb', sub }) => (
  <div style={{ background: '#1f2937', borderRadius: 8, padding: '14px 18px', flex: 1, minWidth: 120 }}>
    <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 4 }}>{label}</div>
    <div style={{ color, fontSize: 20, fontWeight: 700 }}>{value}</div>
    {sub && <div style={{ color: '#6b7280', fontSize: 11 }}>{sub}</div>}
  </div>
);

// ─── Direction breakdown row ────────────────────────────────────────────────

const DirectionRow: React.FC<{ m: DirectionMetrics; color: string }> = ({ m, color }) => (
  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <td style={{ padding: '8px 12px', fontWeight: 600, color }}>{m.label}</td>
    <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{m.closedCount}</td>
    <td style={{ padding: '8px 12px', color: m.totalPnl >= 0 ? '#4ade80' : '#f87171' }}>{fmtCurrency(m.totalPnl)}</td>
    <td style={{ padding: '8px 12px', color: m.winRate >= 50 ? '#4ade80' : '#f87171' }}>{fmtPct(m.winRate)}</td>
    <td style={{ padding: '8px 12px', color: '#4ade80' }}>{fmtCurrency(m.avgWin)}</td>
    <td style={{ padding: '8px 12px', color: '#f87171' }}>-${m.avgLoss.toFixed(2)}</td>
    <td style={{ padding: '8px 12px', color: m.profitFactor >= 1 ? '#4ade80' : '#f87171' }}>{m.profitFactor === Infinity ? 'n/a' : m.profitFactor.toFixed(2)}</td>
    <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{m.avgHoldHrs < 24 ? `${m.avgHoldHrs.toFixed(1)}h` : `${(m.avgHoldHrs / 24).toFixed(1)}d`}</td>
  </tr>
);

// ─── Main Component ──────────────────────────────────────────────────────────

type Tab = 'overview' | 'direction' | 'strategy' | 'exits';

export const AlgoPortfolio: React.FC = () => {
  const { user, token } = useAuth();
  const [signals, setSignals] = useState<AlgoSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  const fetchTrades = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await algoService.getTrades(token, { limit: 500 });
      setSignals(result.signals);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const algoEnabled = canAccessAutoTrader(user, 'alpaca');

  // Derived stats
  const stats = useMemo(() => {
    const closed = signals.filter(s => s.trade?.status === 'closed' && s.trade.pnl != null);
    const open = signals.filter(s => s.trade?.status === 'open');
    const pnls = closed.map(s => s.trade!.pnl!);
    const wins = pnls.filter(p => p > 0);
    const losses = pnls.filter(p => p < 0);
    const totalPnl = pnls.reduce((a, b) => a + b, 0);
    const grossWins = wins.reduce((a, b) => a + b, 0);
    const grossLosses = Math.abs(losses.reduce((a, b) => a + b, 0));
    const winRate = closed.length > 0 ? (wins.length / closed.length) * 100 : 0;
    const avgTrade = closed.length > 0 ? totalPnl / closed.length : 0;
    const avgWin = wins.length > 0 ? grossWins / wins.length : 0;
    const avgLoss = losses.length > 0 ? grossLosses / losses.length : 0;
    const profitFactor = grossLosses > 0 ? grossWins / grossLosses : grossWins > 0 ? Infinity : 0;
    const bestTrade = pnls.length > 0 ? Math.max(...pnls) : 0;
    const worstTrade = pnls.length > 0 ? Math.min(...pnls) : 0;
    const unrealized = open.reduce((sum, s) => sum + (s.trade?.unrealizedPnl || 0), 0);
    const curve = buildEquityCurve(signals);
    const maxDrawdown = calcMaxDrawdown(curve);
    const sharpe = computeSharpe(signals);

    // Direction metrics
    const allDir = computeDirectionMetrics(signals, 'all');
    const longDir = computeDirectionMetrics(signals, 'long');
    const shortDir = computeDirectionMetrics(signals, 'short');

    // Strategy metrics
    const strategyMetrics = computeStrategyMetrics(signals);

    // Exit reasons
    const exitReasons = computeExitReasons(signals);

    return {
      totalPnl, winRate, wins: wins.length, closedCount: closed.length,
      avgTrade, avgWin, avgLoss, profitFactor,
      bestTrade, worstTrade, unrealized, openCount: open.length,
      curve, maxDrawdown, sharpe,
      allDir, longDir, shortDir, strategyMetrics, exitReasons,
    };
  }, [signals]);

  const netPositive = stats.curve.length > 0 && stats.curve[stats.curve.length - 1].cumPnl >= 0;
  const areaColor = netPositive ? '#4ade80' : '#f87171';
  const ticks = useMemo(() => {
    if (stats.curve.length <= 8) return stats.curve.map(p => p.date);
    const step = Math.max(1, Math.floor(stats.curve.length / 6));
    const t: string[] = [];
    for (let i = 0; i < stats.curve.length; i += step) t.push(stats.curve[i].date);
    return t;
  }, [stats.curve]);

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: 'none',
    background: tab === t ? '#374151' : 'transparent', color: tab === t ? '#f9fafb' : '#6b7280',
  });

  const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#6b7280', borderBottom: '1px solid rgba(255,255,255,0.1)' };

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>
            Performance Analytics
          </h1>
          <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>
            Algo trading performance metrics, direction analysis, and strategy breakdown.
          </p>
        </div>

        {!algoEnabled && (
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📈</div>
            <h2 style={{ color: '#f9fafb' }}>Algo Trading is a Pro Feature</h2>
            <p style={{ color: '#9ca3af' }}>Contact us to enable Algo Trading on your Pro account.</p>
          </div>
        )}

        {algoEnabled && loading && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading analytics...</div>
        )}

        {algoEnabled && error && (
          <div style={{ color: '#f87171', background: '#7f1d1d', borderRadius: 8, padding: 16 }}>{error}</div>
        )}

        {algoEnabled && !loading && !error && signals.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
            No trades yet. Execute trades from the Algo Research page to see performance analytics.
          </div>
        )}

        {algoEnabled && !loading && !error && signals.length > 0 && (
          <>
            {/* Top stat cards — 4 columns */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
              <StatCard label="Total P&L" value={fmtCurrency(stats.totalPnl)} color={stats.totalPnl >= 0 ? '#4ade80' : '#f87171'} sub={`${stats.closedCount} trades`} />
              <StatCard label="Win Rate" value={stats.closedCount > 0 ? fmtPct(stats.winRate) : '—'} color={stats.winRate >= 50 ? '#4ade80' : '#f87171'} sub={`${stats.wins}/${stats.closedCount}`} />
              <StatCard label="Profit Factor" value={stats.profitFactor === Infinity ? 'n/a' : stats.profitFactor.toFixed(2)} color={stats.profitFactor >= 1 ? '#4ade80' : '#f87171'} sub="gross wins / losses" />
              <StatCard label="Sharpe Ratio" value={stats.sharpe !== 0 ? stats.sharpe.toFixed(2) : '—'} color={stats.sharpe >= 1 ? '#4ade80' : stats.sharpe >= 0 ? '#fbbf24' : '#f87171'} sub="annualized" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
              <StatCard label="Avg Win" value={fmtCurrency(stats.avgWin)} color="#4ade80" />
              <StatCard label="Avg Loss" value={stats.avgLoss > 0 ? `-$${stats.avgLoss.toFixed(2)}` : '—'} color="#f87171" />
              <StatCard label="Max Drawdown" value={stats.maxDrawdown > 0 ? `-$${stats.maxDrawdown.toFixed(2)}` : '$0'} color={stats.maxDrawdown > 0 ? '#f87171' : '#6b7280'} />
              <StatCard label="Open Positions" value={stats.openCount > 0 ? `${stats.openCount} (${fmtCurrency(stats.unrealized)})` : '0'} color="#93c5fd" />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: '#111827', borderRadius: 8, padding: 4 }}>
              <button style={tabStyle('overview')} onClick={() => setTab('overview')}>Equity Curve</button>
              <button style={tabStyle('direction')} onClick={() => setTab('direction')}>By Direction</button>
              <button style={tabStyle('strategy')} onClick={() => setTab('strategy')}>By Strategy</button>
              <button style={tabStyle('exits')} onClick={() => setTab('exits')}>Exit Reasons</button>
            </div>

            {/* Tab Content */}
            {tab === 'overview' && stats.curve.length >= 2 && (
              <div style={{ background: '#1f2937', borderRadius: 8, padding: '16px 16px 8px' }}>
                <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, marginBottom: 8 }}>Equity Curve (Cumulative P&L)</div>
                <ChartErrorBoundary>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={stats.curve} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={areaColor} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={areaColor} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" ticks={ticks} tickFormatter={fmtDate} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `$${v.toFixed(0)}`} width={55} />
                      <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="4 4" />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', fontSize: '0.85em' }}
                        labelFormatter={(label: string) => fmtDate(label)}
                        formatter={((value: number | undefined, _name: string, props: any) => {
                          const pt = props.payload as EquityPoint;
                          return [`$${(value ?? 0).toFixed(2)}  (${pt.symbol} ${fmtCurrency(pt.pnl)})`, 'Cumulative P&L'];
                        }) as any}
                      />
                      <Area type="monotone" dataKey="cumPnl" stroke={areaColor} strokeWidth={1.5} fill="url(#equityGrad)" dot={false} activeDot={{ r: 3, fill: areaColor }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartErrorBoundary>
              </div>
            )}

            {tab === 'overview' && stats.curve.length < 2 && (
              <div style={{ background: '#1f2937', borderRadius: 8, padding: 28, textAlign: 'center', color: '#6b7280' }}>
                Need at least 2 closed trades to show equity curve.
              </div>
            )}

            {tab === 'direction' && (
              <div style={{ background: '#1f2937', borderRadius: 8, padding: 16, overflowX: 'auto' }}>
                <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, marginBottom: 12 }}>Performance by Direction</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Direction</th>
                      <th style={thStyle}>Trades</th>
                      <th style={thStyle}>Total P&L</th>
                      <th style={thStyle}>Win Rate</th>
                      <th style={thStyle}>Avg Win</th>
                      <th style={thStyle}>Avg Loss</th>
                      <th style={thStyle}>Profit Factor</th>
                      <th style={thStyle}>Avg Hold</th>
                    </tr>
                  </thead>
                  <tbody>
                    <DirectionRow m={stats.allDir} color="#f9fafb" />
                    {stats.longDir.closedCount > 0 && <DirectionRow m={stats.longDir} color="#4ade80" />}
                    {stats.shortDir.closedCount > 0 && <DirectionRow m={stats.shortDir} color="#f87171" />}
                  </tbody>
                </table>
                {stats.longDir.closedCount === 0 && stats.shortDir.closedCount === 0 && (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: 16 }}>No closed trades with direction data yet.</div>
                )}
              </div>
            )}

            {tab === 'strategy' && (
              <div style={{ background: '#1f2937', borderRadius: 8, padding: 16, overflowX: 'auto' }}>
                <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, marginBottom: 12 }}>Performance by Strategy</div>
                {stats.strategyMetrics.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Strategy</th>
                        <th style={thStyle}>Trades</th>
                        <th style={thStyle}>Win Rate</th>
                        <th style={thStyle}>Avg P&L</th>
                        <th style={thStyle}>Total P&L</th>
                        <th style={thStyle}>Best</th>
                        <th style={thStyle}>Worst</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.strategyMetrics.map(sm => (
                        <tr key={sm.strategyId} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <td style={{ padding: '8px 12px', color: '#a78bfa', fontWeight: 600 }}>{sm.name}</td>
                          <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{sm.closedCount}</td>
                          <td style={{ padding: '8px 12px', color: sm.winRate >= 50 ? '#4ade80' : '#f87171' }}>{fmtPct(sm.winRate)}</td>
                          <td style={{ padding: '8px 12px', color: sm.avgPnl >= 0 ? '#4ade80' : '#f87171' }}>{fmtCurrency(sm.avgPnl)}</td>
                          <td style={{ padding: '8px 12px', color: sm.totalPnl >= 0 ? '#4ade80' : '#f87171', fontWeight: 600 }}>{fmtCurrency(sm.totalPnl)}</td>
                          <td style={{ padding: '8px 12px', color: '#4ade80' }}>{fmtCurrency(sm.bestTrade)}</td>
                          <td style={{ padding: '8px 12px', color: '#f87171' }}>{fmtCurrency(sm.worstTrade)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: 16 }}>No closed trades yet.</div>
                )}
              </div>
            )}

            {tab === 'exits' && (
              <div style={{ background: '#1f2937', borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, marginBottom: 12 }}>Exit Reason Breakdown</div>
                {stats.exitReasons.length > 0 ? (
                  <>
                    <ChartErrorBoundary>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={stats.exitReasons} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <XAxis dataKey="reason" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', fontSize: '0.85em' }}
                            formatter={((value: number, _name: string, props: any) => {
                              const item = props.payload as ExitReasonCount;
                              return [`${value} trades (avg P&L: ${fmtCurrency(item.avgPnl)})`, 'Count'];
                            }) as any}
                          />
                          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                            {stats.exitReasons.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartErrorBoundary>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12 }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Exit Reason</th>
                          <th style={thStyle}>Count</th>
                          <th style={thStyle}>Avg P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.exitReasons.map(er => (
                          <tr key={er.reason} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: er.color, marginRight: 8 }} />
                              <span style={{ color: '#d1d5db', textTransform: 'capitalize' }}>{er.reason}</span>
                            </td>
                            <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{er.count}</td>
                            <td style={{ padding: '8px 12px', color: er.avgPnl >= 0 ? '#4ade80' : '#f87171' }}>{fmtCurrency(er.avgPnl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: 16 }}>No exit data yet.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AlgoPortfolio;
